import React from "react";
import PropTypes from "prop-types";
import moment from "moment-timezone";
import {
  CalendarWrapper,
  CalendarHeader,
  MonthTitle,
  NavigationButtons,
  NavButton,
  CalendarGrid,
  WeekDayRow,
  WeekDay,
  CalendarBody,
  DayCell,
  DayNumber,
  SessionItem,
  SessionTime,
  SessionInfo,
  SessionInfoShort,
} from "./sessionCalendarNew.styled";

const SessionCalendarNewView = ({
  currentDate,
  calendarData,
  onPreviousMonth,
  onNextMonth,
  onSessionClick,
}) => {
  // Obtener el color para cada tipo de sesión (basado en el nombre del curso)
  const getSessionColor = (sessionIndex, session, sessionDate) => {
    // Si es una sesión pasada y no completada, mostrar en rojo
    const isSessionPast = moment(sessionDate).isBefore(moment(), "day");
    const isNotCompleted = session.completed === false;

    if (isSessionPast && isNotCompleted) {
      return { bg: "#fee2e2", border: "#ef4444", text: "#991b1b" }; // Rojo
    }

    if (isSessionPast && !isNotCompleted) {
      return { bg: "#d1fae5", border: "#10b981", text: "#065f46" }; // Verde
    }

    const colors = [
      { bg: "#fef3c7", border: "#f59e0b", text: "#92400e" }, // Amarillo
      { bg: "#e2fefd", border: "#44e9ef", text: "#1b9999" }, // Rojo
      { bg: "#dbeafe", border: "#3b82f6", text: "#1e40af" }, // Azul
      { bg: "#fce7f3", border: "#ec4899", text: "#9f1239" }, // Rosa
      { bg: "#d1fae5", border: "#10b981", text: "#065f46" }, // Verde
    ];
    return colors[sessionIndex % colors.length];
  };

  const renderCalendar = () => {
    // Configurar que la semana empiece en lunes
    moment.locale("es", {
      week: {
        dow: 1, // Lunes es el primer día de la semana
      },
    });

    const startOfMonth = moment(currentDate).startOf("month");
    const endOfMonth = moment(currentDate).endOf("month");
    const startDate = moment(startOfMonth).startOf("week");
    const endDate = moment(endOfMonth).endOf("week");

    const weeks = [];
    let days = [];
    let day = moment(startDate);

    while (day.isSameOrBefore(endDate)) {
      for (let i = 0; i < 7; i++) {
        const dateKey = day.format("YYYY-MM-DD");
        const isCurrentMonth = day.month() === currentDate.month();
        const isToday = day.isSame(moment(), "day");
        const sessions = calendarData[dateKey] || [];
        const dayNumber = day.format("D");

        days.push(
          <DayCell
            key={dateKey}
            $isCurrentMonth={isCurrentMonth}
            $isToday={isToday}
          >
            <DayNumber $isToday={isToday}>{dayNumber}</DayNumber>
            {sessions.length > 0 && (
              <div>
                {sessions.map((session, index) => {
                  const color = getSessionColor(index, session, dateKey);
                  return (
                    <SessionItem
                      key={`${dateKey}-${index}`}
                      $bgColor={color.bg}
                      $borderColor={color.border}
                      $textColor={color.text}
                      onClick={() => onSessionClick(session)}
                    >
                      <SessionTime>{session.time}</SessionTime>
                      <SessionInfo>
                        {`${session.course} - ${session.meeting?.center?.name}`}
                      </SessionInfo>
                      <SessionInfoShort>
                        {session.course.substring(0, 10)}
                      </SessionInfoShort>
                    </SessionItem>
                  );
                })}
              </div>
            )}
          </DayCell>
        );

        day = day.add(1, "day");
      }
      weeks.push(<tr key={`week-${weeks.length}`}>{days}</tr>);
      days = [];
    }

    return weeks;
  };

  // Días de la semana (Lunes a Domingo)
  const weekDays = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

  return (
    <CalendarWrapper>
      <CalendarHeader>
        <MonthTitle>{currentDate.format("MMMM YYYY")}</MonthTitle>
        <NavigationButtons>
          <NavButton onClick={onPreviousMonth}>
            <i className="fas fa-chevron-left"></i>
          </NavButton>
          <NavButton onClick={onNextMonth}>
            <i className="fas fa-chevron-right"></i>
          </NavButton>
        </NavigationButtons>
      </CalendarHeader>

      <CalendarGrid>
        <WeekDayRow>
          <tr>
            {weekDays.map((day) => (
              <WeekDay key={day}>{day}</WeekDay>
            ))}
          </tr>
        </WeekDayRow>

        <CalendarBody>{renderCalendar()}</CalendarBody>
      </CalendarGrid>
    </CalendarWrapper>
  );
};

SessionCalendarNewView.propTypes = {
  currentDate: PropTypes.object.isRequired,
  calendarData: PropTypes.object.isRequired,
  onPreviousMonth: PropTypes.func.isRequired,
  onNextMonth: PropTypes.func.isRequired,
  onSessionClick: PropTypes.func.isRequired,
};

export default SessionCalendarNewView;
